import { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";

const links = [
  { to: "/", label: "Accueil" },
  { to: "/services", label: "Mes services" },
  { to: "/galerie", label: "Galerie" },
  { to: "/a-propos", label: "À propos" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-md bg-up-offwhite/80 border-b border-up-taupe/30"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="container-up flex h-20 items-center justify-between">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="group flex items-center gap-2.5 sm:gap-3 min-w-0"
          onClick={() => setOpen(false)}
        >
          <img
            src="https://customer-assets.emergentagent.com/job_breizh-boost/artifacts/2lsrq7t5_LOGO.jpg"
            alt="Breizh Boost Studio"
            className="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover ring-1 ring-up-taupe/40 shrink-0"
          />
          <span className="flex flex-col leading-tight min-w-0">
            <span className="font-serif text-base sm:text-lg text-up-brown truncate">
              Breizh Boost Studio
            </span>
            <span className="hidden sm:block text-[10px] tracking-[0.28em] uppercase text-up-muted">
              Création · Visibilité · Image
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10" aria-label="Navigation principale">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-").replace("à", "a")}`}
              end={l.to === "/"}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors relative py-1 ${
                  isActive
                    ? "text-up-brown"
                    : "text-up-muted hover:text-up-brown"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute -bottom-1 left-0 h-px bg-up-brown transition-all duration-300 ${
                      isActive ? "w-full" : "w-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <Link
            to="/contact"
            data-testid="navbar-cta-rdv"
            className="btn-primary !py-2.5 !px-6 group"
          >
            Prendre rendez-vous
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        </div>

        <button
          type="button"
          className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-up-taupe/60 text-up-brown"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
          data-testid="navbar-mobile-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div
          className="lg:hidden border-t border-up-taupe/30 bg-up-offwhite"
          data-testid="navbar-mobile-menu"
        >
          <nav className="container-up flex flex-col py-6 gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-").replace("à", "a")}`}
                className={({ isActive }) =>
                  `py-3 text-lg font-serif tracking-tight border-b border-up-taupe/20 ${
                    isActive ? "text-up-brown" : "text-up-muted"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="btn-primary mt-6 self-start"
              data-testid="mobile-cta-rdv"
            >
              Prendre rendez-vous
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
